import { logger } from '@/lib/logger';
import { FEATURES } from '@/config/environment';

// PostHog is dynamically imported only when analytics is enabled
// This saves ~100KB from bundle when disabled
type PostHogType = typeof import('posthog-js').default;
let posthog: PostHogType | null = null;

// PostHog configuration
// Use reverse proxy to bypass ad blockers - requests go to /ingest/* which Vercel proxies to PostHog
// Exception: All preview deployments use direct URL because Vercel Auth blocks the proxy
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;

// Detect if we're on a Vercel preview deployment (any non-production URL)
// This runs at module load time, so we need to be defensive about window access
function getIsPreviewDeployment(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return (
    hostname === 'staging.subvention360.com' ||
    hostname.includes('vercel.app') ||
    hostname === 'localhost'
  );
}

const isPreviewDeployment = getIsPreviewDeployment();

// Production uses proxy to bypass ad blockers, preview uses direct URL (blocked by ad blockers but works with Vercel Auth)
const POSTHOG_API_HOST = isPreviewDeployment ? 'https://eu.i.posthog.com' : '/ingest';
const POSTHOG_UI_HOST = 'https://eu.posthog.com'; // For surveys/toolbars

// Log detection results immediately for debugging
if (typeof window !== 'undefined') {
  console.log('[PostHog] Environment detection:', {
    hostname: window.location.hostname,
    isPreviewDeployment,
    apiHost: POSTHOG_API_HOST
  });
}

// Initialize PostHog
let posthogInitialized = false;

export async function initPostHog(): Promise<void> {
  // Early exit if analytics disabled - don't even load the library
  if (!FEATURES.ANALYTICS_ENABLED) {
    console.log('[PostHog] Analytics disabled - skipping initialization');
    return;
  }

  // Diagnostic logging for production debugging
  console.log('[PostHog] Init called:', {
    hasKey: !!POSTHOG_KEY,
    keyPrefix: POSTHOG_KEY ? POSTHOG_KEY.substring(0, 10) + '...' : 'MISSING',
    analyticsEnabled: FEATURES.ANALYTICS_ENABLED,
    alreadyInit: posthogInitialized
  });

  if (posthogInitialized || !POSTHOG_KEY) {
    console.log('[PostHog] Skipping init:', {
      reason: posthogInitialized ? 'already initialized' : 'no key'
    });
    return;
  }

  try {
    // Dynamic import - only loads ~100KB library when actually needed
    const posthogModule = await import('posthog-js');
    posthog = posthogModule.default;

    console.log('[PostHog] Calling posthog.init with host:', POSTHOG_API_HOST);
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_API_HOST,
      ui_host: POSTHOG_UI_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false, // We handle this manually
      capture_pageleave: true,
      autocapture: false, // We use explicit events
      persistence: 'localStorage',
      disable_session_recording: true, // Enable later if needed
      loaded: () => {
        console.log('[PostHog] ✅ Initialized successfully via proxy');
        logger.info('PostHog initialized successfully via proxy');
        posthogInitialized = true;
      },
    });
  } catch (error) {
    console.error('[PostHog] ❌ Failed to initialize:', error);
    logger.error('Failed to initialize PostHog:', error);
  }
}

// Identify user for PostHog
export function identifyUser(userId: string, properties?: Record<string, any>): void {
  if (!posthogInitialized || !FEATURES.ANALYTICS_ENABLED || !posthog) return;

  try {
    posthog.identify(userId, properties);
  } catch (error) {
    logger.error('PostHog identify failed:', error);
  }
}

// Reset user identity (on logout)
export function resetUser(): void {
  if (!posthogInitialized || !posthog) return;

  try {
    posthog.reset();
  } catch (error) {
    logger.error('PostHog reset failed:', error);
  }
}

export interface AnalyticsEvent {
  event: string;
  page?: string;
  userId?: string;
  properties?: Record<string, any>;
  timestamp?: number;
}

/**
 * Core funnel events for subvention360
 */
export enum FunnelEvents {
  // Trial & Conversion Flow
  TRIAL_CTA_CLICKED = 'trial_cta_clicked',
  SIGNUP_PAGE_VIEWED = 'signup_page_viewed',
  SIGNUP_FORM_SUBMITTED = 'signup_form_submitted',
  SIGNUP_COMPLETED = 'signup_completed',

  // Authentication Flow
  AUTH_START = 'auth_started',
  AUTH_SUCCESS = 'auth_successful',
  AUTH_FAILED = 'auth_failed',
  
  // Profile Management
  PROFILE_CREATED = 'profile_created',
  PROFILE_VIEWED = 'profile_viewed',
  PROFILE_UPDATED = 'profile_updated',
  
  // Document Management  
  DOCUMENT_UPLOADED = 'document_uploaded',
  DOCUMENT_EXTRACTION_STARTED = 'document_extraction_started',
  DOCUMENT_EXTRACTION_COMPLETED = 'document_extraction_completed',
  DOCUMENT_EXTRACTION_FAILED = 'document_extraction_failed',
  
  // Subsidy Search & Discovery
  SUBSIDY_SEARCH_STARTED = 'subsidy_search_started',
  SUBSIDY_SEARCH_RESULTS_VIEWED = 'subsidy_search_results_viewed',
  SUBSIDY_DETAIL_VIEWED = 'subsidy_detail_viewed',
  SUBSIDY_APPLICATION_STARTED = 'subsidy_application_started',
  
  // Application Flow
  APPLICATION_FORM_OPENED = 'application_form_opened',
  APPLICATION_AI_PREFILL_USED = 'application_ai_prefill_used',
  APPLICATION_SUBMITTED = 'application_submitted',
  APPLICATION_DRAFT_SAVED = 'application_draft_saved',
  
  // Calendar & Deadlines
  CALENDAR_VIEWED = 'calendar_viewed',
  DEADLINE_REMINDER_SET = 'deadline_reminder_set',
  
  // Admin & Analytics
  ADMIN_DASHBOARD_VIEWED = 'admin_dashboard_viewed',
  ANALYTICS_DASHBOARD_VIEWED = 'analytics_dashboard_viewed',
  
  // Feedback Events
  FEEDBACK_SUBMITTED = 'feedback_submitted',
  ERROR_REPORTED = 'error_reported',
  DATA_CORRECTED = 'data_corrected',

  // Performance & Errors
  PAGE_LOAD_SLOW = 'page_load_slow',
  ERROR_OCCURRED = 'error_occurred',

  // Onboarding & Role Selection
  ROLE_SELECTION_VIEWED = 'role_selection_viewed',
  ROLE_SELECTED = 'role_selected',
  ROLE_CHANGED = 'role_changed',
  ONBOARDING_STARTED = 'onboarding_started',
  ONBOARDING_STEP_VIEWED = 'onboarding_step_viewed',
  ONBOARDING_STEP_COMPLETED = 'onboarding_step_completed',
  ONBOARDING_COMPLETED = 'onboarding_completed',
  ONBOARDING_ABANDONED = 'onboarding_abandoned',

  // Onboarding Checklist
  CHECKLIST_VIEWED = 'checklist_viewed',
  CHECKLIST_MINIMIZED = 'checklist_minimized',
  CHECKLIST_DISMISSED = 'checklist_dismissed',
  CHECKLIST_ITEM_CLICKED = 'checklist_item_clicked',
  CHECKLIST_COMPLETED = 'checklist_completed',

  // Help Widget
  HELP_WIDGET_OPENED = 'help_widget_opened',
  HELP_WIDGET_CLOSED = 'help_widget_closed',
  HELP_ARTICLE_VIEWED = 'help_article_viewed',
  HELP_SEARCH_PERFORMED = 'help_search_performed',
  HELP_LIVE_CHAT_CLICKED = 'help_live_chat_clicked',
}

class Analytics {
  private events: AnalyticsEvent[] = [];
  
  /**
   * Track a user action with optional properties
   */
  track(event: string | FunnelEvents, properties?: Record<string, any>): void {
    if (!FEATURES.ANALYTICS_ENABLED) {
      return;
    }

    const analyticsEvent: AnalyticsEvent = {
      event: event.toString(),
      properties,
      timestamp: Date.now()
    };

    // Store event locally
    this.events.push(analyticsEvent);

    // Log for debugging
    logger.debug(`Analytics: ${event}`, properties);

    // Send to PostHog
    this.sendToPostHog(event.toString(), properties);
  }

  /**
   * Track funnel progression
   */
  trackFunnel(step: FunnelEvents, properties?: Record<string, any>): void {
    this.track(step, {
      ...properties,
      funnel_step: step,
      session_id: this.getSessionId()
    });
  }

  /**
   * Track page views with performance metrics
   * Uses PostHog's standard $pageview event for compatibility with built-in insights
   */
  trackPageView(pageName: string, loadTime?: number): void {
    this.track('$pageview', {
      $current_url: window.location.href,
      $pathname: window.location.pathname,
      $host: window.location.host,
      $referrer: document.referrer,
      page_name: pageName,
      load_time: loadTime,
      user_agent: navigator.userAgent,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
    });

    // Track slow page loads
    if (loadTime && loadTime > 2000) {
      this.track(FunnelEvents.PAGE_LOAD_SLOW, {
        page: pageName,
        load_time: loadTime
      });
    }
  }

  /**
   * Track errors with context
   */
  trackError(error: Error, context?: Record<string, any>): void {
    this.track(FunnelEvents.ERROR_OCCURRED, {
      error_message: error.message,
      error_stack: error.stack,
      ...context
    });
  }

  /**
   * Get session ID (simple implementation)
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Get all tracked events (for debugging)
   */
  getEvents(): AnalyticsEvent[] {
    return [...this.events];
  }

  /**
   * Clear tracked events
   */
  clearEvents(): void {
    this.events = [];
  }

  /**
   * Send events to PostHog
   */
  private sendToPostHog(eventName: string, properties?: Record<string, any>): void {
    if (!posthogInitialized || !posthog) return;

    try {
      posthog.capture(eventName, {
        ...properties,
        session_id: this.getSessionId(),
      });
    } catch (error) {
      logger.error('PostHog capture failed:', error);
    }
  }
}

export const analytics = new Analytics();

/**
 * React hook for analytics tracking
 */
export const useAnalytics = () => {
  return {
    track: analytics.track.bind(analytics),
    trackFunnel: analytics.trackFunnel.bind(analytics),
    trackPageView: analytics.trackPageView.bind(analytics),
    trackError: analytics.trackError.bind(analytics)
  };
};